import { hydrateGanttLaneMode, hydrateGanttTimeDisplay, hydrateGanttTimeZoom } from "./ganttBoard";
import type { ReleasePackItem, ReleasePackItemId, ReleasePackView } from "./releasePack";
import type {
  BoardFilterPreset,
  CompanyProfile,
  CreateDefaultKind,
  DashboardDensity,
  DashboardHero,
  DeskBehaviorPrefs,
  DeskCreateKind,
  DeskReleaseCriticalId,
  GanttTimeDisplay,
} from "./types";

export const DESK_RELEASE_CRITICAL_IDS: readonly DeskReleaseCriticalId[] = [
  "identity",
  "ofp",
  "support",
  "weather",
  "performance",
  "airworthiness",
];

/** Fail-closed critical set when the snapshot omits the list. */
export const DEFAULT_RELEASE_CRITICAL_ITEMS: DeskReleaseCriticalId[] = [
  "identity",
  "ofp",
  "performance",
  "airworthiness",
];

/**
 * Fail-closed to today’s desk: lean Dashboard, Gantt aircraft + Local,
 * OCC follows Gantt (0.3.96), Release gate matches 0.3.99, chips on, Create charter.
 */
export const DEFAULT_DESK_BEHAVIOR: DeskBehaviorPrefs = {
  dashboardHero: "gantt",
  dashboardDensity: "comfortable",
  showNeedsInbox: true,
  showNeedsSupport: true,
  showNeedsConflicts: true,
  showNeedsRelease: false,
  showNeedsAog: true,
  ganttLaneMode: "aircraft",
  ganttShowLegend: true,
  ganttShowDutyBands: true,
  ganttTimeZoom: 1,
  occClockFollowGantt: true,
  occClockDisplay: "zulu",
  releaseSoftBlockReady: true,
  releaseCriticalItems: [...DEFAULT_RELEASE_CRITICAL_ITEMS],
  searchIncludeActions: true,
  searchIncludeSettings: true,
  searchIncludeTickets: true,
  searchIncludeAircraft: true,
  searchIncludeCrew: true,
  searchIncludePages: true,
  searchShortcutEnabled: true,
  searchShowHeader: true,
  searchOpenOnLoad: false,
  searchShowGroupsOnOpen: true,
  boardChipSupport: true,
  boardChipRelease: true,
  boardChipDivert: true,
  createDefaultKind: "charter",
  lastCreateKind: "charter",
  preferRustCareerFlush: false,
  widgetEditorEnabled: false,
  boardFilterPresets: [],
  boardDayNotes: {},
};

function hydrateBool(raw: unknown, fallback: boolean): boolean {
  return typeof raw === "boolean" ? raw : fallback;
}

function hydrateHero(raw: unknown): DashboardHero {
  return raw === "map" ? "map" : "gantt";
}

function hydrateDensity(raw: unknown): DashboardDensity {
  return raw === "compact" ? "compact" : "comfortable";
}

function hydrateCreateDefault(raw: unknown): CreateDefaultKind {
  if (raw === "ferry" || raw === "remember" || raw === "charter") return raw;
  return "charter";
}

function hydrateCreateKind(raw: unknown): DeskCreateKind {
  return raw === "ferry" ? "ferry" : "charter";
}

function isCriticalId(value: unknown): value is DeskReleaseCriticalId {
  return typeof value === "string" && (DESK_RELEASE_CRITICAL_IDS as readonly string[]).includes(value);
}

export function hydrateReleaseCriticalItems(raw: unknown): DeskReleaseCriticalId[] {
  if (!Array.isArray(raw)) return [...DEFAULT_RELEASE_CRITICAL_ITEMS];
  const out: DeskReleaseCriticalId[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!isCriticalId(row) || seen.has(row)) continue;
    seen.add(row);
    out.push(row);
  }
  return out;
}

export function hydrateDeskBehavior(raw?: Partial<DeskBehaviorPrefs> | null): DeskBehaviorPrefs {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    dashboardHero: hydrateHero(src.dashboardHero),
    dashboardDensity: hydrateDensity(src.dashboardDensity),
    showNeedsInbox: hydrateBool(src.showNeedsInbox, DEFAULT_DESK_BEHAVIOR.showNeedsInbox),
    showNeedsSupport: hydrateBool(src.showNeedsSupport, DEFAULT_DESK_BEHAVIOR.showNeedsSupport),
    showNeedsConflicts: hydrateBool(src.showNeedsConflicts, DEFAULT_DESK_BEHAVIOR.showNeedsConflicts),
    showNeedsRelease: hydrateBool(src.showNeedsRelease, DEFAULT_DESK_BEHAVIOR.showNeedsRelease),
    showNeedsAog: hydrateBool(src.showNeedsAog, DEFAULT_DESK_BEHAVIOR.showNeedsAog),
    ganttLaneMode: hydrateGanttLaneMode(src.ganttLaneMode ?? DEFAULT_DESK_BEHAVIOR.ganttLaneMode),
    ganttShowLegend: hydrateBool(src.ganttShowLegend, DEFAULT_DESK_BEHAVIOR.ganttShowLegend),
    ganttShowDutyBands: hydrateBool(src.ganttShowDutyBands, DEFAULT_DESK_BEHAVIOR.ganttShowDutyBands),
    ganttTimeZoom: hydrateGanttTimeZoom(src.ganttTimeZoom ?? DEFAULT_DESK_BEHAVIOR.ganttTimeZoom),
    occClockFollowGantt: hydrateBool(src.occClockFollowGantt, DEFAULT_DESK_BEHAVIOR.occClockFollowGantt),
    occClockDisplay:
      src.occClockDisplay === "zulu" || src.occClockDisplay === "local"
        ? src.occClockDisplay
        : DEFAULT_DESK_BEHAVIOR.occClockDisplay,
    releaseSoftBlockReady: hydrateBool(src.releaseSoftBlockReady, DEFAULT_DESK_BEHAVIOR.releaseSoftBlockReady),
    releaseCriticalItems: hydrateReleaseCriticalItems(src.releaseCriticalItems),
    searchIncludeActions: hydrateBool(src.searchIncludeActions, DEFAULT_DESK_BEHAVIOR.searchIncludeActions),
    searchIncludeSettings: hydrateBool(src.searchIncludeSettings, DEFAULT_DESK_BEHAVIOR.searchIncludeSettings),
    searchIncludeTickets: hydrateBool(src.searchIncludeTickets, DEFAULT_DESK_BEHAVIOR.searchIncludeTickets),
    searchIncludeAircraft: hydrateBool(src.searchIncludeAircraft, DEFAULT_DESK_BEHAVIOR.searchIncludeAircraft),
    searchIncludeCrew: hydrateBool(src.searchIncludeCrew, DEFAULT_DESK_BEHAVIOR.searchIncludeCrew),
    searchIncludePages: hydrateBool(src.searchIncludePages, DEFAULT_DESK_BEHAVIOR.searchIncludePages),
    searchShortcutEnabled: hydrateBool(src.searchShortcutEnabled, DEFAULT_DESK_BEHAVIOR.searchShortcutEnabled),
    searchShowHeader: hydrateBool(src.searchShowHeader, DEFAULT_DESK_BEHAVIOR.searchShowHeader),
    searchOpenOnLoad: hydrateBool(src.searchOpenOnLoad, DEFAULT_DESK_BEHAVIOR.searchOpenOnLoad),
    searchShowGroupsOnOpen: hydrateBool(src.searchShowGroupsOnOpen, DEFAULT_DESK_BEHAVIOR.searchShowGroupsOnOpen),
    boardChipSupport: hydrateBool(src.boardChipSupport, DEFAULT_DESK_BEHAVIOR.boardChipSupport),
    boardChipRelease: hydrateBool(src.boardChipRelease, DEFAULT_DESK_BEHAVIOR.boardChipRelease),
    boardChipDivert: hydrateBool(src.boardChipDivert, DEFAULT_DESK_BEHAVIOR.boardChipDivert),
    createDefaultKind: hydrateCreateDefault(src.createDefaultKind),
    lastCreateKind: hydrateCreateKind(src.lastCreateKind),
    preferRustCareerFlush: hydrateBool(src.preferRustCareerFlush, false),
    widgetEditorEnabled: hydrateBool(src.widgetEditorEnabled, false),
    boardFilterPresets: hydrateBoardPresets(src.boardFilterPresets),
    boardDayNotes: hydrateDayNotes(src.boardDayNotes),
  };
}

function hydrateBoardPresets(raw: unknown): BoardFilterPreset[] {
  if (!Array.isArray(raw)) return [];
  const out: BoardFilterPreset[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const item = row as Partial<BoardFilterPreset>;
    const name = typeof item.name === "string" ? item.name.trim() : "";
    const id = typeof item.id === "string" ? item.id.trim() : "";
    if (!name || !id) continue;
    out.push({
      id,
      name,
      query: typeof item.query === "string" ? item.query : "",
      todayOnly: item.todayOnly === true,
      airborneOnly: item.airborneOnly === true,
      unassignedOnly: item.unassignedOnly === true,
      activeOnly: item.activeOnly === true,
      showFerry: item.showFerry !== false,
      showMaintenance: item.showMaintenance !== false,
      showCancelled: item.showCancelled === true,
      aircraftIds: Array.isArray(item.aircraftIds) ? item.aircraftIds.filter((id): id is string => typeof id === "string") : [],
    });
  }
  return out;
}

function hydrateDayNotes(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key) || typeof value !== "string") continue;
    const text = value.trim();
    if (text) out[key] = text.slice(0, 240);
  }
  return out;
}

export function deskBehaviorOf(
  company?: Partial<Pick<CompanyProfile, "deskBehavior">> | null,
): DeskBehaviorPrefs {
  return hydrateDeskBehavior(company?.deskBehavior);
}

export function patchDeskBehavior(
  company: Partial<Pick<CompanyProfile, "deskBehavior">> | null | undefined,
  patch: Partial<DeskBehaviorPrefs>,
): DeskBehaviorPrefs {
  return hydrateDeskBehavior({ ...deskBehaviorOf(company), ...patch });
}

export function resetDeskBehaviorPatch(): Pick<CompanyProfile, "deskBehavior" | "ganttTimeDisplay"> {
  return {
    deskBehavior: { ...DEFAULT_DESK_BEHAVIOR, releaseCriticalItems: [...DEFAULT_RELEASE_CRITICAL_ITEMS] },
    ganttTimeDisplay: "local",
  };
}

export function resolveCreateKind(prefs: DeskBehaviorPrefs): DeskCreateKind {
  if (prefs.createDefaultKind === "remember") return prefs.lastCreateKind;
  return prefs.createDefaultKind;
}

export function resolveOccTimeDisplay(
  prefs: DeskBehaviorPrefs,
  ganttTimeDisplay?: GanttTimeDisplay | null,
): GanttTimeDisplay {
  if (prefs.occClockFollowGantt) return hydrateGanttTimeDisplay(ganttTimeDisplay);
  return hydrateGanttTimeDisplay(prefs.occClockDisplay);
}

export function releaseReadyMissingCritical(
  view: Pick<ReleasePackView, "items">,
  prefs: Pick<DeskBehaviorPrefs, "releaseSoftBlockReady" | "releaseCriticalItems">,
): ReleasePackItem[] {
  if (!prefs.releaseSoftBlockReady) return [];
  const critical = new Set(prefs.releaseCriticalItems);
  return view.items.filter((item) => critical.has(item.id as ReleasePackItemId) && item.state === "missing");
}
